import React, { Component } from 'react'
import { injectIntl, FormattedMessage } from 'react-intl'
import PropTypes from 'prop-types'
import cx from 'classnames'
import _ from 'lodash'

import { FilterTable } from 'gnos-tables'

import CustomDate from '../../../../../../../../../../../components/CustomDate'
import NoData from '../../../../../../../../../components/Workspace/components/NoData'
import InactivityIndicatorHeader from '../../../../../../../../../../../components/TableCell/components/InactiveIndicator/Header'
import InactiveIndicatorCell from '../../../../../../../../../../../components/TableCell/components/InactiveIndicator/Cell'
import FailedLoad from '../../../../../../../../../components/Workspace/components/FailedLoad'

import { LOCALE_PREFIX } from '../../constants'

import hocConnect from './hocConnect'
import './style.scss'

@injectIntl
@hocConnect
export default class RiskyTaxSubject extends Component {
    static propTypes = {
        block: PropTypes.string.isRequired,
        customData: PropTypes.object,
        data: PropTypes.array,
        dataLocale: PropTypes.string,
        failed: PropTypes.bool,
        id: PropTypes.string.isRequired,
        intl: PropTypes.object,
        isLoading: PropTypes.bool,
        level: PropTypes.string.isRequired,
        loaded: PropTypes.bool,
        locale: PropTypes.string.isRequired,
        locked: PropTypes.bool,
        module: PropTypes.string.isRequired,
        negativeEventsLoadTableData: PropTypes.func.isRequired,
        negativeEventsTableChange: PropTypes.func.isRequired,
        pagination: PropTypes.object,
        filter: PropTypes.object,
        sorted: PropTypes.array,
        tableId: PropTypes.string.isRequired,
        tableSelector: PropTypes.string.isRequired,
    }

    componentDidMount() {
        const { loaded, dataLocale, locale } = this.props

        if (!loaded || dataLocale !== locale) this.loadData()
    }

    shouldComponentUpdate(nextProps) {
        const {
            customData,
            data,
            isLoading,
            level,
            locale,
            sorted,
            pagination,
            loaded,
            failed,
            tableId,
            tableSelector,
            filter,
        } = this.props

        if (!_.isEqual(customData, nextProps.customData)) return true
        if (!_.isEqual(data, nextProps.data)) return true
        if (!_.isEqual(isLoading, nextProps.isLoading)) return true
        if (!_.isEqual(level, nextProps.level)) return true
        if (!_.isEqual(locale, nextProps.locale)) return true
        if (!_.isEqual(sorted, nextProps.sorted)) return true
        if (!_.isEqual(pagination, nextProps.pagination)) return true
        if (!_.isEqual(loaded, nextProps.loaded)) return true
        if (!_.isEqual(failed, nextProps.failed)) return true
        if (!_.isEqual(tableId, nextProps.tableId)) return true
        if (!_.isEqual(tableSelector, nextProps.tableSelector)) return true
        if (!_.isEqual(filter, nextProps.filter)) return true

        return false
    }

    componentDidUpdate(prevProps) {
        const { level, locale, dataLocale } = this.props

        if (prevProps.level !== level) this.loadData()
        if (locale !== dataLocale && dataLocale) this.loadData()
    }

    loadData = () => {
        const { id, module, block, level, negativeEventsLoadTableData, tableId, tableSelector, customData } = this.props

        negativeEventsLoadTableData(
            tableId,
            tableSelector,
            id, // Entity ID
            module,
            block,
            level,
            customData
        )
    }

    handleTableChange = (changeData) => {
        const { negativeEventsTableChange, tableId, tableSelector, id, module, block, level, customData } = this.props

        negativeEventsTableChange(tableId, tableSelector, id, module, block, level, customData, changeData)
    }

    render() {
        const { intl, tableId, data, sorted, pagination, isLoading, failed, locked, filter } = this.props

        if (locked && !isLoading && !failed) {
            return (
                <div className={'ne-risky-tax-subject'}>
                    <NoData text={intl.formatMessage({ id: `${LOCALE_PREFIX}.lockedTab` })} />
                </div>
            )
        }

        // Failed data load
        if (failed && !isLoading) {
            return (
                <div className={'ne-risky-tax-subject'}>
                    <FailedLoad
                        customCallback={this.loadData}
                        customText={intl.formatMessage({ id: 'global.failedToLoadTabContent' })}
                        show={failed}
                    />
                </div>
            )
        }

        // Empty data
        if (_.isEmpty(data) && !isLoading && _.isEmpty(filter?.activeFilters)) {
            return (
                <div className={'ne-risky-tax-subject'}>
                    <NoData text={intl.formatMessage({ id: `${LOCALE_PREFIX}.noData` })} />
                </div>
            )
        }

        return (
            <div className={'ne-risky-tax-subject'}>
                <div className={'table-container'}>
                    <FilterTable
                        id={tableId}
                        data={data ?? []}
                        loading={isLoading}
                        columnsDefinition={[
                            {
                                id: 'activity',
                                accessor: 'inactive',
                                Header: ({ Controls }) => (
                                    <>
                                        {Controls}
                                        <InactivityIndicatorHeader />
                                    </>
                                ),
                                width: 35,
                                Cell: (value) => <InactiveIndicatorCell inactive={_.get(value, 'value', false)} />,
                                filterable: true,
                                filter: {
                                    type: 'VALUE',
                                    value: _.map(
                                        _.get(filter, 'availableParameters.activity.availableValues', []),
                                        (x) => ({
                                            value: _.get(x, 'value', null),
                                            item: _.get(x, 'title', null),
                                        })
                                    ),
                                },
                            },
                            {
                                accessor: 'state',
                                Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.event` }),
                                Cell: (value) => (
                                    <div className={cx(_.get(value, 'original.inactive') && 'inactive')}>
                                        {_.get(value, 'value', '')}
                                        {_.get(value, 'original.violationDate.value', null) && (
                                            <span>
                                                <FormattedMessage id={`${LOCALE_PREFIX}.violationDate`} />
                                                <CustomDate
                                                    date={_.get(value, 'original.violationDate.value', null)}
                                                    precision={_.get(value, 'original.violationDate.precision', null)}
                                                />
                                            </span>
                                        )}
                                    </div>
                                ),
                            },
                            {
                                accessor: 'publicationDate',
                                Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.dateFrom` }),
                                Cell: (value) => (
                                    <div className={cx(_.get(value, 'original.inactive') && 'inactive')}>
                                        <CustomDate
                                            date={_.get(value, 'value.value', null)}
                                            precision={_.get(value, 'value.precision', null)}
                                        />
                                    </div>
                                ),
                                width: 125,
                                sortable: true,
                            },
                            {
                                accessor: 'endDate',
                                Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.dateTo` }),
                                Cell: (value) => (
                                    <div className={cx(_.get(value, 'original.inactive') && 'inactive')}>
                                        <CustomDate
                                            date={_.get(value, 'value.value', null)}
                                            precision={_.get(value, 'value.precision', null)}
                                        />
                                    </div>
                                ),
                                width: 125,
                                sortable: true,
                            },
                        ]}
                        showPagination={false}
                        settings={{
                            pagination,
                            sorted,
                            filtered: (filter?.activeFilters ?? []).map((actFilter) => ({
                                id: actFilter?.id,
                                value: actFilter?.value || [],
                            })),
                        }}
                        pageSizeOptions={[5, 10, 20, 50, 100]}
                        onTableChange={this.handleTableChange}
                        texts={{
                            pagination: {
                                page: intl.formatMessage({ id: 'global.page' }),
                                from: intl.formatMessage({ id: 'global.of' }),
                                rowCount: intl.formatMessage({ id: 'global.rowCount' }),
                                rowShow: intl.formatMessage({ id: 'global.rowShow' }),
                                all: intl.formatMessage({ id: 'global.all' }),
                            },
                            filter: {
                                valueTitle: intl.formatMessage({ id: 'global.valueTitle' }),
                                fulltextTitle: intl.formatMessage({ id: 'global.fulltextTitle' }),
                            },
                        }}
                    />
                </div>
            </div>
        )
    }
}
